/**
 * Drone D1 #319 + #321 — operator-side missions section (Operator Mode shell). Lists missions
 * ASSIGNED to this operator and drives the operator transitions: accept WITH TERMS (requested →
 * accepted, capturing ETA + quoted price + a live-video feed link), decline (requested →
 * declined), enroute (confirmed → enroute, after the requestor confirms), and flown (enroute →
 * flown). Available actions come from the shared state machine (availableMissionActions), so an
 * illegal edge is never offered; the server re-validates and is the actor-guard wall. The server
 * scribes each transition (no client-side double audit). Operators never see the requestor-scoped
 * billing ledger.
 *
 * COLOUR DISCIPLINE (Law 1): the Operator shell stays neutral slate — urgency (a request awaiting
 * the operator) is signalled in the AMBER family + an explicit label, never reserved red/green.
 */
import { useEffect, useState, type FormEvent } from 'react';
import {
  DRONE_FEED_TYPES,
  availableMissionActions,
  type DroneFeedType,
  type DroneRequestUrgency,
  type MissionStatus,
} from '@tsr/shared-types';
import {
  acceptOperatorMission,
  declineOperatorMission,
  enrouteOperatorMission,
  fetchOperatorMissionMessages,
  fetchOperatorMissions,
  flyOperatorMission,
  markOperatorMissionMessageRead,
  postOperatorMissionMessage,
  type AcceptMissionTermsInput,
  type OperatorMissionView,
} from '../api';
import { MissionThreadPanel } from '../components/MissionThreadPanel';
import { aoFromCapabilities, capabilitiesSummary, missionTermsSummary } from '../droneMissionUi';
import { hazardStyle } from '../styles/incidentStyles';
import { Chip, PortalSection, ghostButtonClass, primaryButtonClass } from './portalUi';

/**
 * Phase 4e urgency badge. COLOUR DISCIPLINE (Law 1, see header): the Operator shell stays
 * neutral slate and signals urgency in the AMBER family + an explicit label — NEVER reserved
 * red/green — so 'immediate' escalates within amber (brighter + bolder) rather than going red.
 * 'routine' reads as a quiet muted chip (the default, present so the operator sees the level was
 * declared, but not attention-grabbing).
 */
const URGENCY_BADGE: Record<DroneRequestUrgency, { label: string; cls: string }> = {
  routine: { label: 'Routine', cls: 'border-slate-600 bg-slate-700/40 text-slate-400' },
  urgent: { label: 'Urgent', cls: 'border-amber-500/50 bg-amber-500/10 text-amber-300' },
  immediate: { label: 'Immediate', cls: 'border-amber-400 bg-amber-400/20 text-amber-200 font-semibold' },
};

function urgencyBadge(urgency: string): { label: string; cls: string } {
  return URGENCY_BADGE[urgency as DroneRequestUrgency] ?? URGENCY_BADGE.routine;
}

function statusPill(status: MissionStatus): { text: string; cls: string } {
  if (status === 'requested') {
    return { text: 'Awaiting your response', cls: 'text-amber-300 border-amber-500/50 bg-amber-500/10' };
  }
  const labels: Record<MissionStatus, string> = {
    requested: 'Requested',
    accepted: 'Accepted — awaiting confirm',
    confirmed: 'Confirmed — dispatch',
    enroute: 'Enroute',
    flown: 'Flown',
    billed: 'Billed',
    paid: 'Paid',
    declined: 'Declined',
    cancelled: 'Cancelled',
  };
  return { text: labels[status] ?? status, cls: 'text-slate-300 border-slate-600' };
}

const inputCls =
  'w-full rounded border border-slate-700 bg-slate-950/60 px-2 py-1 text-xs text-slate-100 placeholder:text-slate-500 focus:border-slate-500 focus:outline-none';

/** Inline accept-with-terms form (#321). Captures ETA + quoted price + the feed link; the URL
 *  is shape-validated server-side (captured, not fetched — #322 resolves it). */
function AcceptTermsForm({
  busy,
  onSubmit,
  onCancel,
}: {
  busy: boolean;
  onSubmit: (terms: AcceptMissionTermsInput) => void;
  onCancel: () => void;
}): JSX.Element {
  const [eta, setEta] = useState('');
  const [price, setPrice] = useState('');
  const [onStation, setOnStation] = useState('');
  const [feedUrl, setFeedUrl] = useState('');
  const [feedType, setFeedType] = useState<DroneFeedType>('hls');

  const etaNum = Number(eta);
  const priceNum = Number(price);
  const onStationTrim = onStation.trim();
  const onStationNum = Number(onStationTrim);
  const onStationValid =
    onStationTrim === '' || (Number.isInteger(onStationNum) && onStationNum > 0);
  const valid =
    Number.isInteger(etaNum) &&
    etaNum > 0 &&
    Number.isFinite(priceNum) &&
    priceNum >= 0 &&
    onStationValid &&
    feedUrl.trim().length > 0;

  function submit(e: FormEvent): void {
    e.preventDefault();
    if (!valid) return;
    onSubmit({
      eta_minutes: etaNum,
      quoted_price_cad: priceNum,
      offered_on_station_minutes: onStationTrim === '' ? null : onStationNum,
      feed_url: feedUrl.trim(),
      feed_type: feedType,
    });
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-2 rounded-md border border-slate-700 bg-slate-900/60 p-2"
      data-testid="accept-terms-form"
    >
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wide text-slate-400">
          ETA (minutes)
          <input
            type="number"
            min={1}
            value={eta}
            onChange={(e) => setEta(e.target.value)}
            placeholder="e.g. 25"
            className={inputCls}
            data-testid="accept-eta"
          />
        </label>
        <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wide text-slate-400">
          Quoted price (CAD)
          <input
            type="number"
            min={0}
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="e.g. 1200"
            className={inputCls}
            data-testid="accept-price"
          />
        </label>
        <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wide text-slate-400">
          Eyes-on (min, optional)
          <input
            type="number"
            min={1}
            value={onStation}
            onChange={(e) => setOnStation(e.target.value)}
            placeholder="e.g. 120"
            className={inputCls}
            data-testid="accept-on-station"
          />
        </label>
      </div>
      <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wide text-slate-400">
        Live feed link
        <input
          type="url"
          value={feedUrl}
          onChange={(e) => setFeedUrl(e.target.value)}
          placeholder="https://… or rtsp://…"
          className={inputCls}
          data-testid="accept-feed-url"
        />
      </label>
      <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wide text-slate-400">
        Feed type
        <select
          value={feedType}
          onChange={(e) => setFeedType(e.target.value as DroneFeedType)}
          className={inputCls}
          data-testid="accept-feed-type"
        >
          {DRONE_FEED_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy || !valid}
          className={primaryButtonClass}
          data-testid="accept-submit"
        >
          Accept with terms
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className={ghostButtonClass}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function MissionsSection(): JSX.Element {
  const [missions, setMissions] = useState<OperatorMissionView[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);

  async function reload(): Promise<void> {
    try {
      setMissions(await fetchOperatorMissions());
    } catch {
      /* keep current */
    }
  }
  useEffect(() => {
    void reload();
  }, []);

  function applyUpdate(updated: OperatorMissionView): void {
    setMissions((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
  }

  async function acceptWithTerms(id: string, terms: AcceptMissionTermsInput): Promise<void> {
    setBusyId(id);
    try {
      applyUpdate(await acceptOperatorMission(id, terms));
      setAcceptingId(null);
    } catch {
      void reload();
    } finally {
      setBusyId(null);
    }
  }

  async function act(id: string, action: 'decline' | 'enroute' | 'flown'): Promise<void> {
    setBusyId(id);
    try {
      const updated =
        action === 'decline'
          ? await declineOperatorMission(id)
          : action === 'enroute'
            ? await enrouteOperatorMission(id)
            : await flyOperatorMission(id);
      applyUpdate(updated);
    } catch {
      void reload();
    } finally {
      setBusyId(null);
    }
  }

  return (
    <PortalSection title="Missions" testId="portal-missions">
      {missions.length === 0 ? (
        <p className="text-sm text-slate-500">No missions assigned to you yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {missions.map((m) => {
            const pill = statusPill(m.status);
            const actions = availableMissionActions(m.status, 'operator');
            const terms = missionTermsSummary(m);
            const isAccepting = acceptingId === m.id;
            return (
              <li
                key={m.id}
                className="flex flex-col gap-2 rounded-md border border-slate-800 bg-slate-950/40 p-3"
                data-testid="operator-mission-row"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-100">
                      {m.requestor_name ?? 'Requesting agency'}
                    </p>
                    <p className="truncate text-xs text-slate-400">
                      {m.asset_name ?? 'Asset TBD'} · {capabilitiesSummary(m.requested_capabilities)}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${pill.cls}`}
                  >
                    {pill.text}
                  </span>
                </div>
                {/* Phase 4e (§I.2 ☎4) — the decision context the operator needs to triage
                    accept/decline IN the COP: how urgent, the requested watch window, the incident
                    it serves (name · # · hazard), and where. Closes the phone reach for context. */}
                {(() => {
                  const urgency = urgencyBadge(m.requested_urgency);
                  const hz = m.incident_name ? hazardStyle(m.hazard_type) : null;
                  // Location for "where to fly": the incident centroid when incident-linked, else the
                  // requestor's drawn area-of-interest point (always carried on requested_capabilities.ao
                  // — closes the §I.2 ☎4 location reach for an UNLINKED request too).
                  const aoPoint = aoFromCapabilities(m.requested_capabilities);
                  const loc =
                    m.incident_lat != null && m.incident_lon != null
                      ? { lat: m.incident_lat, lon: m.incident_lon, label: 'Incident location' }
                      : aoPoint
                        ? { lat: aoPoint.lat, lon: aoPoint.lon, label: 'Requested area' }
                        : null;
                  return (
                    <div
                      className="flex flex-wrap items-center gap-1.5 text-[11px]"
                      data-testid="operator-mission-context"
                    >
                      <span
                        className={`rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${urgency.cls}`}
                        data-testid="operator-mission-urgency"
                        data-urgency={m.requested_urgency}
                      >
                        {urgency.label}
                      </span>
                      {m.requested_on_station_minutes != null && (
                        <span className="text-slate-300" data-testid="operator-mission-eyes-on">
                          {m.requested_on_station_minutes} min eyes-on requested
                        </span>
                      )}
                      {hz && (
                        <span className="text-slate-300" data-testid="operator-mission-incident">
                          {hz.icon} {m.incident_name}
                          {m.incident_number ? ` · ${m.incident_number}` : ''} · {hz.label}
                        </span>
                      )}
                      {loc && (
                        <span
                          className="font-mono text-slate-500"
                          data-testid="operator-mission-location"
                          title={loc.label}
                        >
                          {loc.lat.toFixed(3)}, {loc.lon.toFixed(3)}
                        </span>
                      )}
                    </div>
                  );
                })()}
                {terms && <p className="text-xs text-slate-300" data-testid="mission-terms">{terms}</p>}
                {m.declined_reason && (
                  <p className="text-xs text-slate-400">Declined: {m.declined_reason}</p>
                )}
                {isAccepting ? (
                  <AcceptTermsForm
                    busy={busyId === m.id}
                    onSubmit={(t) => void acceptWithTerms(m.id, t)}
                    onCancel={() => setAcceptingId(null)}
                  />
                ) : (
                  actions.length > 0 && (
                    <div className="flex items-center gap-2">
                      {actions.includes('accept') && (
                        <button
                          type="button"
                          disabled={busyId === m.id}
                          onClick={() => setAcceptingId(m.id)}
                          className={primaryButtonClass}
                          data-testid="mission-accept"
                        >
                          Accept
                        </button>
                      )}
                      {actions.includes('decline') && (
                        <button
                          type="button"
                          disabled={busyId === m.id}
                          onClick={() => void act(m.id, 'decline')}
                          className={ghostButtonClass}
                        >
                          Decline
                        </button>
                      )}
                      {actions.includes('enroute') && (
                        <button
                          type="button"
                          disabled={busyId === m.id}
                          onClick={() => void act(m.id, 'enroute')}
                          className={primaryButtonClass}
                          data-testid="mission-enroute"
                        >
                          Mark enroute
                        </button>
                      )}
                      {actions.includes('flown') && (
                        <button
                          type="button"
                          disabled={busyId === m.id}
                          onClick={() => void act(m.id, 'flown')}
                          className={primaryButtonClass}
                        >
                          Mark flown
                        </button>
                      )}
                    </div>
                  )
                )}
                <div className="flex items-center justify-between gap-2">
                  <Chip>{`Requested ${m.requested_at ? m.requested_at.slice(0, 10) : '—'}`}</Chip>
                  <button
                    type="button"
                    onClick={() => setOpenThreadId((cur) => (cur === m.id ? null : m.id))}
                    className={`${ghostButtonClass} flex items-center gap-1.5`}
                    data-testid="mission-messages-toggle"
                  >
                    Messages
                    {m.unread_count > 0 && (
                      <span
                        className="inline-flex min-w-4 items-center justify-center rounded-full bg-amber-500/90 px-1 text-[10px] font-semibold text-slate-950"
                        data-testid="mission-unread-badge"
                      >
                        {m.unread_count}
                      </span>
                    )}
                  </button>
                </div>
                {openThreadId === m.id && (
                  <MissionThreadPanel
                    missionId={m.id}
                    viewerRole="operator"
                    load={(signal) => fetchOperatorMissionMessages(m.id, signal)}
                    send={(content) => postOperatorMissionMessage(m.id, content)}
                    markRead={(msgId) => markOperatorMissionMessageRead(m.id, msgId)}
                    onReadMarked={() => void reload()}
                    className="pt-2"
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </PortalSection>
  );
}
